import { useEffect } from 'react';

type Size = 'md' | 'lg' | 'xl';

const SIZE_CLASS: Record<Size, string> = {
  md: 'max-w-md',
  lg: 'max-w-lg',
  xl: 'max-w-2xl',
};

/**
 * Shared modal shell. Clicking outside the card or pressing Escape closes
 * unless `onClose` is omitted (useful while a submission is in-flight).
 * Locks body scroll while open so the backdrop actually feels like a layer.
 */
export function Modal({
  title,
  subtitle,
  size = 'lg',
  onClose,
  children,
}: {
  title: string;
  subtitle?: React.ReactNode;
  size?: Size;
  onClose?: () => void;
  children: React.ReactNode;
}) {
  useEffect(() => {
    if (!onClose) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  return (
    <div
      className="fixed inset-0 z-40 flex items-start justify-center bg-slate-900/60 backdrop-blur-sm p-4 sm:p-6 overflow-y-auto"
      onClick={onClose}
    >
      <div
        className={
          'w-full ' +
          SIZE_CLASS[size] +
          ' mt-[5vh] bg-white rounded-xl shadow-2xl ring-1 ring-slate-200 animate-[fadeIn_.12s_ease-out]'
        }
        onClick={(e) => e.stopPropagation()}
      >
        <header className="px-6 py-4 border-b border-slate-200 flex items-start justify-between gap-4">
          <div className="min-w-0">
            <h2 className="text-lg font-semibold text-slate-900 leading-tight">
              {title}
            </h2>
            {subtitle && (
              <div className="mt-0.5 text-sm text-slate-600 truncate">
                {subtitle}
              </div>
            )}
          </div>
          {onClose && (
            <button
              className="btn-ghost h-7 w-7 text-slate-500 -mt-1 -mr-2"
              aria-label="Close"
              onClick={onClose}
              type="button"
            >
              ✕
            </button>
          )}
        </header>
        <div className="px-6 py-5">{children}</div>
      </div>
    </div>
  );
}
